import * as Sentry from '@sentry/react';
import { QueryClient } from '@tanstack/query-core';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { APIProvider } from '@vis.gl/react-google-maps';
import { isRouteErrorResponse, Links, Meta, Outlet, Scripts, ScrollRestoration } from 'react-router';
import { ToastContainer } from 'react-toastify';

import i18n from './plugins/i18n';

import type { Route } from './+types/root';

import './app.css';
import './plugins/i18n';
import 'react-toastify/dist/ReactToastify.css';

export function Layout({ children }: { children: React.ReactNode }) {
    return (
        <html lang={i18n.language}>
            <head>
                <meta charSet={'utf-8'} />

                {/* JSON-LD Structured Data */}
                <script type={'application/ld+json'} />

                <meta
                    name={'viewport'}
                    content={'width=device-width, initial-scale=1'}
                />

                {/* Search engine crawling instructions */}
                <meta
                    name={'robots'}
                    content={'index, follow'}
                />

                <link
                    href={'/favicon.svg'}
                    rel={'icon'}
                />

                <link
                    href={'https://fonts.googleapis.com'}
                    rel={'preconnect'}
                />

                <link
                    href={'https://fonts.gstatic.com'}
                    rel={'preconnect'}
                />

                <link
                    href={
                        'https://fonts.googleapis.com/css2?family=Allura&family=Poppins:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap'
                    }
                    rel={'stylesheet'}
                />

                <Meta />

                <Links />
            </head>

            <body>
                {children}

                <ScrollRestoration />

                <Scripts />
            </body>
        </html>
    );
}

const queryClient = new QueryClient();

Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_ENVIRONMENT,
    sendDefaultPii: false,
});

export default function App() {
    return (
        <APIProvider
            apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
            libraries={['places']}
        >
            <QueryClientProvider client={queryClient}>
                <ReactQueryDevtools initialIsOpen={false} />

                <div className={'flex max-w-screen flex-col items-center overflow-x-hidden'}>
                    <ToastContainer />

                    <main className={'w-full'}>
                        <Outlet />
                    </main>
                </div>
            </QueryClientProvider>
        </APIProvider>
    );
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
    let message = 'Oops!';
    let details = 'An unexpected error occurred.';
    let stack: string | undefined;

    if (isRouteErrorResponse(error)) {
        message = error.status === 404 ? '404' : 'Error';
        details = error.status === 404 ? 'The requested page could not be found.' : error.statusText || details;
    } else if (import.meta.env.DEV && error && error instanceof Error) {
        details = error.message;
        stack = error.stack;
    }

    return (
        <main className={'container mx-auto p-4 pt-16'}>
            <h1>{message}</h1>

            <p>{details}</p>

            {stack && (
                <pre className={'w-full overflow-x-auto p-4'}>
                    <code>{stack}</code>
                </pre>
            )}
        </main>
    );
}
